import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Card,
  Row,
  Col,
  Badge,
  Tab,
  Tabs,
  Table,
  Spinner,
  Alert,
} from "react-bootstrap";
import { formatDistance } from "date-fns";
import pt from "date-fns/locale/pt-BR";
import {
  IconUser,
  IconPhone,
  IconMail,
  IconMapPin,
  IconCalendar,
  IconEdit,
  IconTrash,
  IconTag,
  IconInfoCircle,
  IconUsers,
  IconMessageCircle,
  IconCopy,
  IconFileText,
  IconHeart,
  IconMan,
  IconBuildingSkyscraper,
  IconId,
  IconBrandWhatsapp,
  IconClock,
} from "@tabler/icons-react";
import ReactMarkdown from "react-markdown";
import { getCustomer } from "../services/CustomerServices";
import { formatCNPJ, formatCPF, formatPhone } from "../utils";

const CustomerDetails = ({ show, onHide, customer, onEdit, onDelete }) => {
  const [customerData, setCustomerData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    if (show && customer?.id) {
      fetchCustomerDetails();
    }
  }, [show, customer]);

  const fetchCustomerDetails = async () => {
    setLoading(true);
    try {
      const response = await getCustomer(customer.id);
      setCustomerData(response.data);
    } catch (error) {
      console.error("Erro ao buscar detalhes do cliente:", error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    // Aqui você pode adicionar uma notificação de "copiado"
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: "success",
      inactive: "secondary",
      blocked: "danger",
      potential: "warning",
    };

    const labels = {
      active: "Ativo",
      inactive: "Inativo",
      blocked: "Bloqueado",
      potential: "Potencial",
    };

    return (
      <Badge bg={variants[status] || "secondary"}>
        {labels[status] || status}
      </Badge>
    );
  };

  const getPersonTypeBadge = (personType) => {
    return (
      <Badge bg={personType === "PF" ? "info" : "primary"}>
        {personType === "PF" ? "Pessoa Física" : "Pessoa Jurídica"}
      </Badge>
    );
  };

  const getGenderLabel = (gender) => {
    const labels = {
      M: "Masculino",
      F: "Feminino",
      O: "Outro",
      N: "Não informado",
    };
    return labels[gender] || gender;
  };

  const getMaritalStatusLabel = (status) => {
    const labels = {
      single: "Solteiro(a)",
      married: "Casado(a)",
      divorced: "Divorciado(a)",
      widowed: "Viúvo(a)",
      other: "Outro",
    };
    return labels[status] || status;
  };

  const getAcquisitionChannelLabel = (channel) => {
    const labels = {
      website: "Site/Website",
      whatsapp: "WhatsApp",
      instagram: "Instagram",
      facebook: "Facebook",
      google: "Google/Busca",
      referral: "Indicação",
      walk_in: "Entrada Direta",
      advertising: "Publicidade",
      other: "Outro",
    };
    return labels[channel] || channel;
  };

  // Função para formatar data e hora
  const formatDateTime = (dateTimeString) => {
    const date = new Date(dateTimeString);
    return {
      date: date.toLocaleDateString("pt-BR"),
      time: date.toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };
  };

  // Função para obter variante do badge baseado no status do agendamento
  const getAppointmentStatusVariant = (status) => {
    switch (status) {
      case "scheduled":
        return "primary";
      case "completed":
        return "success";
      case "cancelled":
        return "danger";
      case "in_progress":
        return "warning";
      default:
        return "secondary";
    }
  };

  // Função para traduzir status do agendamento
  const translateAppointmentStatus = (status) => {
    const statusMap = {
      scheduled: "Agendado",
      completed: "Concluído",
      cancelled: "Cancelado",
      in_progress: "Em Andamento",
      pending: "Pendente",
    };
    return statusMap[status] || status;
  };

  // Função para traduzir confirmação
  const translateConfirmation = (confirmation) => {
    const confirmationMap = {
      pending: "Pendente",
      confirmed: "Confirmado",
      cancelled: "Cancelado",
    };
    return confirmationMap[confirmation] || confirmation;
  };

  if (!customerData && !loading) {
    return null;
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center">
          <IconUser className="me-2" />
          {customerData?.full_name || "Detalhes do Cliente"}
          {customerData && (
            <div className="ms-3">{getStatusBadge(customerData.status)}</div>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div className="text-center p-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Carregando detalhes...</p>
          </div>
        ) : customerData ? (
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="mb-3"
          >
            {/* Aba Visão Geral */}
            <Tab eventKey="overview" title="Visão Geral">
              <Row>
                <Col md={8}>
                  <Card className="h-100">
                    <Card.Header>
                      <h6 className="mb-0 d-flex align-items-center">
                        <IconInfoCircle className="me-2" />
                        Informações Básicas
                      </h6>
                    </Card.Header>
                    <Card.Body>
                      <Row className="mb-3">
                        <Col sm={4}>
                          <strong>Nome Completo:</strong>
                        </Col>
                        <Col sm={8}>
                          {customerData.full_name}
                          {customerData.nickname && (
                            <small className="text-muted ms-2">
                              ({customerData.nickname})
                            </small>
                          )}
                        </Col>
                      </Row>

                      <Row className="mb-3">
                        <Col sm={4}>
                          <strong>Tipo:</strong>
                        </Col>
                        <Col sm={8}>
                          {getPersonTypeBadge(customerData.person_type)}
                        </Col>
                      </Row>

                      {customerData.person_type === "PF" &&
                        customerData.cpf && (
                          <Row className="mb-3">
                            <Col sm={4}>
                              <strong>CPF:</strong>
                            </Col>
                            <Col sm={8} className="d-flex align-items-center">
                              <IconId className="me-2 text-muted" size={16} />
                              {customerData.cpf_formatted ||
                                formatCPF(customerData.cpf)}
                              <Button
                                variant="link"
                                size="sm"
                                className="p-0 ms-2"
                                onClick={() =>
                                  copyToClipboard(customerData.cpf)
                                }
                              >
                                <IconCopy size={14} />
                              </Button>
                            </Col>
                          </Row>
                        )}

                      {customerData.person_type === "PJ" &&
                        customerData.cnpj && (
                          <Row className="mb-3">
                            <Col sm={4}>
                              <strong>CNPJ:</strong>
                            </Col>
                            <Col sm={8} className="d-flex align-items-center">
                              <IconBuildingSkyscraper
                                className="me-2 text-muted"
                                size={16}
                              />
                              {customerData.cnpj_formatted ||
                                formatCNPJ(customerData.cnpj)}
                              <Button
                                variant="link"
                                size="sm"
                                className="p-0 ms-2"
                                onClick={() =>
                                  copyToClipboard(customerData.cnpj)
                                }
                              >
                                <IconCopy size={14} />
                              </Button>
                            </Col>
                          </Row>
                        )}

                      {customerData.birth_date && (
                        <Row className="mb-3">
                          <Col sm={4}>
                            <strong>Data de Nascimento:</strong>
                          </Col>
                          <Col sm={8} className="d-flex align-items-center">
                            <IconCalendar
                              className="me-2 text-muted"
                              size={16}
                            />
                            {new Date(
                              customerData.birth_date
                            ).toLocaleDateString("pt-BR")}
                            {customerData.age && (
                              <small className="ms-2 text-muted">
                                ({customerData.age} anos)
                              </small>
                            )}
                          </Col>
                        </Row>
                      )}

                      {customerData.gender && customerData.gender !== "N" && (
                        <Row className="mb-3">
                          <Col sm={4}>
                            <strong>Gênero:</strong>
                          </Col>
                          <Col sm={8} className="d-flex align-items-center">
                            <IconMan className="me-2 text-muted" size={16} />
                            {getGenderLabel(customerData.gender)}
                          </Col>
                        </Row>
                      )}

                      {customerData.marital_status && (
                        <Row className="mb-3">
                          <Col sm={4}>
                            <strong>Estado Civil:</strong>
                          </Col>
                          <Col sm={8} className="d-flex align-items-center">
                            <IconHeart className="me-2 text-muted" size={16} />
                            {getMaritalStatusLabel(customerData.marital_status)}
                          </Col>
                        </Row>
                      )}
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={4}>
                  <Card className="h-100">
                    <Card.Header>
                      <h6 className="mb-0 d-flex align-items-center">
                        <IconPhone className="me-2" />
                        Contato
                      </h6>
                    </Card.Header>
                    <Card.Body>
                      <div className="mb-3">
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center">
                            <IconPhone className="me-2 text-muted" size={16} />
                            <strong>Principal:</strong>
                          </div>
                          {customerData.is_whatsapp_active && (
                            <IconBrandWhatsapp
                              size={16}
                              className="text-success"
                            />
                          )}
                        </div>
                        <div className="d-flex align-items-center">
                          {customerData.phone_primary_formatted ||
                            formatPhone(customerData.phone_primary)}
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 ms-2"
                            onClick={() =>
                              copyToClipboard(customerData.phone_primary)
                            }
                          >
                            <IconCopy size={14} />
                          </Button>
                        </div>
                      </div>

                      {customerData.phone_secondary && (
                        <div className="mb-3">
                          <div className="d-flex align-items-center">
                            <IconPhone className="me-2 text-muted" size={16} />
                            <strong>Secundário:</strong>
                          </div>
                          <div className="d-flex align-items-center">
                            {customerData.phone_secondary_formatted ||
                              formatPhone(customerData.phone_secondary)}
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 ms-2"
                              onClick={() =>
                                copyToClipboard(customerData.phone_secondary)
                              }
                            >
                              <IconCopy size={14} />
                            </Button>
                          </div>
                        </div>
                      )}

                      {customerData.email && (
                        <div className="mb-3">
                          <div className="d-flex align-items-center">
                            <IconMail className="me-2 text-muted" size={16} />
                            <strong>E-mail:</strong>
                          </div>
                          <div className="d-flex align-items-center">
                            <a
                              href={`mailto:${customerData.email}`}
                              className="text-decoration-none"
                            >
                              {customerData.email}
                            </a>
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 ms-2"
                              onClick={() =>
                                copyToClipboard(customerData.email)
                              }
                            >
                              <IconCopy size={14} />
                            </Button>
                          </div>
                        </div>
                      )}

                      <div className="mb-3">
                        <div className="d-flex align-items-center">
                          <IconMessageCircle
                            className="me-2 text-muted"
                            size={16}
                          />
                          <strong>Preferência:</strong>
                        </div>
                        <div>
                          <Badge bg="info">
                            {customerData.communication_preference_display ||
                              customerData.communication_preference}
                          </Badge>
                        </div>
                      </div>

                      <div className="d-flex gap-2 flex-wrap">
                        {customerData.accepts_marketing && (
                          <Badge bg="success">Aceita Marketing</Badge>
                        )}
                        {customerData.is_whatsapp_active && (
                          <Badge bg="success">WhatsApp Ativo</Badge>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Row className="mt-3">
                <Col md={6}>
                  <Card>
                    <Card.Header>
                      <h6 className="mb-0 d-flex align-items-center">
                        <IconMapPin className="me-2" />
                        Endereço
                      </h6>
                    </Card.Header>
                    <Card.Body>
                      {customerData.full_address ? (
                        <div>
                          <p className="mb-0">{customerData.full_address}</p>
                          {customerData.zip_code && (
                            <small className="text-muted">
                              CEP: {customerData.zip_code}
                            </small>
                          )}
                        </div>
                      ) : (
                        <p className="text-muted mb-0">
                          Endereço não informado
                        </p>
                      )}
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={6}>
                  <Card>
                    <Card.Header>
                      <h6 className="mb-0 d-flex align-items-center">
                        <IconUsers className="me-2" />
                        Estatísticas
                      </h6>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col xs={6}>
                          <div className="text-center">
                            <h5 className="text-primary mb-0">
                              {customerData.total_appointments}
                            </h5>
                            <small className="text-muted">Agendamentos</small>
                          </div>
                        </Col>
                        <Col xs={6}>
                          <div className="text-center">
                            <h5 className="text-info mb-0">
                              {customerData.total_conversations}
                            </h5>
                            <small className="text-muted">Conversas</small>
                          </div>
                        </Col>
                      </Row>

                      <hr />

                      <Row>
                        <Col xs={6}>
                          <small className="text-muted">Cliente desde:</small>
                          <div>
                            {new Date(customerData.created_at).toLocaleString()}
                          </div>
                        </Col>
                        <Col xs={6}>
                          {customerData.last_contact_date && (
                            <>
                              <small className="text-muted">
                                Último contato:
                              </small>
                              <div>
                                {formatDistance(
                                  new Date(customerData.last_contact_date),
                                  new Date(),
                                  {
                                    addSuffix: true,
                                    locale: pt,
                                  }
                                )}
                              </div>
                            </>
                          )}
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
                {customerData?.ai_notes && (
                  <Col md={12}>
                    <Card>
                      <Card.Header>
                        <h6 className="mb-0 d-flex align-items-center">
                          <IconMapPin className="me-2" />
                          Resumo IA
                        </h6>
                      </Card.Header>
                      <Card.Body>
                        <ReactMarkdown>
                          {customerData.ai_notes}
                        </ReactMarkdown>
                      </Card.Body>
                    </Card>
                  </Col>
                )}
              </Row>
            </Tab>

            {/* Aba Informações Comerciais */}
            <Tab eventKey="commercial" title="Informações Comerciais">
              <Card>
                <Card.Body>
                  <Table responsive borderless>
                    <tbody>
                      <tr>
                        <td width="25%">
                          <strong>Tipo de Cliente:</strong>
                        </td>
                        <td>
                          <Badge bg="primary">
                            {customerData.customer_type_display ||
                              customerData.customer_type}
                          </Badge>
                        </td>
                      </tr>

                      {customerData.acquisition_channel && (
                        <tr>
                          <td>
                            <strong>Canal de Aquisição:</strong>
                          </td>
                          <td>
                            {getAcquisitionChannelLabel(
                              customerData.acquisition_channel
                            )}
                          </td>
                        </tr>
                      )}

                      {customerData.referral_source && (
                        <tr>
                          <td>
                            <strong>Fonte da Indicação:</strong>
                          </td>
                          <td>{customerData.referral_source}</td>
                        </tr>
                      )}

                      {customerData.preferred_schedule && (
                        <tr>
                          <td>
                            <strong>Horário Preferencial:</strong>
                          </td>
                          <td>{customerData.preferred_schedule}</td>
                        </tr>
                      )}

                      {customerData.tags.length > 0 && (
                        <tr>
                          <td>
                            <strong>Tags:</strong>
                          </td>
                          <td>
                            <div className="d-flex flex-wrap gap-1">
                              {customerData.tags
                                .split(",")
                                .map((tag, index) => (
                                  <Badge
                                    key={index}
                                    bg="secondary"
                                    className="me-1"
                                  >
                                    <IconTag size={12} className="me-1" />
                                    {tag.trim()}
                                  </Badge>
                                ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>

              {customerData.notes && (
                <Card className="mt-3">
                  <Card.Header>
                    <h6 className="mb-0 d-flex align-items-center">
                      <IconFileText className="me-2" />
                      Observações
                    </h6>
                  </Card.Header>
                  <Card.Body>
                    <p className="mb-0">{customerData.notes}</p>
                  </Card.Body>
                </Card>
              )}
            </Tab>

            {/* Aba Agendamentos */}
            {customerData.appointments && (
              <Tab eventKey="appointments" title="Agendamentos">
                <div className="mb-4">
                  {/* Cards de Resumo */}
                  <Row className="mb-3">
                    <Col md={3}>
                      <Card className="text-center border-primary">
                        <Card.Body>
                          <div className="d-flex align-items-center justify-content-center mb-2">
                            <IconCalendar
                              size={24}
                              className="text-primary me-2"
                            />
                            <h5 className="mb-0">Total</h5>
                          </div>
                          <h3 className="text-primary mb-0">
                            {customerData.total_appointments_found || 0}
                          </h3>
                          <small className="text-muted">Agendamentos</small>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3}>
                      <Card className="text-center border-info">
                        <Card.Body>
                          <div className="d-flex align-items-center justify-content-center mb-2">
                            <IconClock size={24} className="text-info me-2" />
                            <h5 className="mb-0">Último</h5>
                          </div>
                          <div className="text-info">
                            {customerData.appointments_stats
                              ?.last_appointment ? (
                              <small>
                                {
                                  formatDateTime(
                                    customerData.appointments_stats
                                      .last_appointment
                                  ).date
                                }
                              </small>
                            ) : (
                              <small>Nenhum</small>
                            )}
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3}>
                      <Card className="text-center border-success">
                        <Card.Body>
                          <div className="d-flex align-items-center justify-content-center mb-2">
                            <h5 className="mb-0">Valor Total</h5>
                          </div>
                          <h4 className="text-success mb-0">
                            R${" "}
                            {customerData.appointments_stats?.total_value?.toFixed(
                              2
                            ) || "0,00"}
                          </h4>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3}>
                      <Card className="text-center border-warning">
                        <Card.Body>
                          <div className="d-flex align-items-center justify-content-center mb-2">
                            <h5 className="mb-0">Status</h5>
                          </div>
                          <div>
                            {customerData.appointments_stats
                              ?.status_distribution ? (
                              Object.entries(
                                customerData.appointments_stats
                                  .status_distribution
                              ).map(([status, count]) => (
                                <Badge
                                  key={status}
                                  bg={getAppointmentStatusVariant(status)}
                                  className="me-1"
                                >
                                  {translateAppointmentStatus(status)}: {count}
                                </Badge>
                              ))
                            ) : (
                              <small className="text-muted">Nenhum</small>
                            )}
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>

                  {/* Lista de Agendamentos */}
                  {customerData.appointments &&
                  customerData.appointments.length > 0 ? (
                    <Card>
                      <Card.Header>
                        <h5 className="mb-0 d-flex align-items-center">
                          <IconCalendar className="me-2" />
                          Lista de Agendamentos
                        </h5>
                      </Card.Header>
                      <Card.Body>
                        <Table responsive striped>
                          <thead>
                            <tr>
                              <th>Título</th>
                              <th>Data</th>
                              <th>Horário</th>
                              <th>Status</th>
                              <th>Confirmação</th>
                              <th>Preço</th>
                              <th>Criado em</th>
                            </tr>
                          </thead>
                          <tbody>
                            {customerData.appointments.map(
                              (appointment, index) => {
                                const startDateTime = formatDateTime(
                                  appointment.start
                                );
                                const endDateTime = formatDateTime(
                                  appointment.end
                                );
                                const createdDateTime = formatDateTime(
                                  appointment.created_at
                                );

                                return (
                                  <tr key={index}>
                                    <td className="fs-14">
                                      {appointment.title}
                                      {appointment.manual_schedule === 1 ? (
                                        <Badge
                                          bg="info"
                                          className="ms-2"
                                          size="sm"
                                        >
                                          Manual
                                        </Badge>
                                      ) : (
                                        <Badge
                                          bg="danger"
                                          className="ms-2"
                                          size="sm"
                                        >
                                          IA
                                        </Badge>
                                      )}
                                    </td>
                                    <td className="fs-14">
                                      {startDateTime.date}
                                    </td>
                                    <td className="fs-14">
                                      {startDateTime.time} - {endDateTime.time}
                                    </td>
                                    <td>
                                      <Badge
                                        bg={getAppointmentStatusVariant(
                                          appointment.status
                                        )}
                                      >
                                        {translateAppointmentStatus(
                                          appointment.status
                                        )}
                                      </Badge>
                                    </td>
                                    <td>
                                      <Badge
                                        bg={
                                          appointment.confirmation ===
                                          "confirmed"
                                            ? "success"
                                            : appointment.confirmation ===
                                              "cancelled"
                                            ? "danger"
                                            : "warning"
                                        }
                                      >
                                        {translateConfirmation(
                                          appointment.confirmation
                                        )}
                                      </Badge>
                                    </td>
                                    <td className="fs-14">
                                      {appointment.price ? (
                                        <span className="text-success fw-bold">
                                          R$ {appointment.price.toFixed(2)}
                                        </span>
                                      ) : (
                                        <span className="text-muted">-</span>
                                      )}
                                    </td>
                                    <td className="fs-14">
                                      <small>
                                        {createdDateTime.date} às{" "}
                                        {createdDateTime.time}
                                      </small>
                                    </td>
                                  </tr>
                                );
                              }
                            )}
                          </tbody>
                        </Table>
                      </Card.Body>
                    </Card>
                  ) : (
                    <Alert variant="info" className="text-center">
                      <IconCalendar size={48} className="mb-3 text-muted" />
                      <h5>Nenhum agendamento encontrado</h5>
                      <p className="mb-0">
                        Este cliente ainda não possui agendamentos registrados.
                      </p>
                    </Alert>
                  )}
                </div>
              </Tab>
            )}

            {/* Aba Contato de Emergência */}
            {(customerData.emergency_contact_name ||
              customerData.emergency_contact_phone) && (
              <Tab eventKey="emergency" title="Contato de Emergência">
                <Card>
                  <Card.Body>
                    <Table responsive borderless>
                      <tbody>
                        {customerData.emergency_contact_name && (
                          <tr>
                            <td width="25%">
                              <strong>Nome:</strong>
                            </td>
                            <td>{customerData.emergency_contact_name}</td>
                          </tr>
                        )}

                        {customerData.emergency_contact_phone && (
                          <tr>
                            <td>
                              <strong>Telefone:</strong>
                            </td>
                            <td className="d-flex align-items-center">
                              {formatPhone(
                                customerData.emergency_contact_phone
                              )}
                              <Button
                                variant="link"
                                size="sm"
                                className="p-0 ms-2"
                                onClick={() =>
                                  copyToClipboard(
                                    customerData.emergency_contact_phone
                                  )
                                }
                              >
                                <IconCopy size={14} />
                              </Button>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Tab>
            )}

            {/* Aba Dados Customizados */}
            {customerData.metadata &&
              Object.keys(customerData.metadata).length > 0 && (
                <Tab eventKey="metadata" title="Dados Customizados">
                  <Card>
                    <Card.Body>
                      <Table responsive borderless>
                        <tbody>
                          {Object.entries(customerData.metadata).map(
                            ([key, value]) => (
                              <tr key={key}>
                                <td width="25%">
                                  <strong>
                                    {key.replace(/_/g, " ").toUpperCase()}:
                                  </strong>
                                </td>
                                <td>{value}</td>
                              </tr>
                            )
                          )}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                </Tab>
              )}
          </Tabs>
        ) : (
          <Alert variant="warning">
            <IconInfoCircle className="me-2" />
            Não foi possível carregar os detalhes do cliente.
          </Alert>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Fechar
        </Button>
        {customerData && (
          <>
            <Button
              variant="outline-primary"
              onClick={() => {
                onEdit(customerData);
                onHide();
              }}
              className="d-flex align-items-center"
            >
              <IconEdit size={18} className="me-1" />
              Editar
            </Button>
            <Button
              variant="outline-danger"
              onClick={() => {
                onDelete(customerData);
                onHide();
              }}
              className="d-flex align-items-center"
            >
              <IconTrash size={18} className="me-1" />
              Inativar
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default CustomerDetails;
